import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';
import type { HistoryOrder, PassportTab } from '../types/passport';

interface UseHistoryDataOptions {
  effectiveToken: string | null;
  passportTab: PassportTab;
}

export function useHistoryData(options: UseHistoryDataOptions) {
  const { effectiveToken, passportTab } = options;

  const [historyOrders, setHistoryOrders] = useState<HistoryOrder[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false);

  const fetchHistory = useCallback(async (page = 1, append = false) => {
    if (!effectiveToken) return;
    if (append) setHistoryLoadingMore(true);
    else setHistoryLoading(true);
    try {
      const { data: hd } = await axios.get(
        `${API_URL || ''}/api/auth/customer/orders?page=${page}&limit=20`,
        { headers: { Authorization: `Bearer ${effectiveToken}` } }
      );
      if (append) {
        setHistoryOrders(prev => [...prev, ...hd.orders]);
      } else {
        setHistoryOrders(hd.orders);
      }
      setHistoryTotal(hd.total);
      setHistoryPage(hd.page);
    } catch { /* silently fail */ }
    finally {
      setHistoryLoading(false);
      setHistoryLoadingMore(false);
    }
  }, [effectiveToken]);

  useEffect(() => {
    if (passportTab === 'history' && historyOrders.length === 0 && effectiveToken) {
      fetchHistory(1);
    }
  }, [passportTab, historyOrders.length, effectiveToken, fetchHistory]);

  return {
    historyOrders,
    historyPage,
    historyTotal,
    historyLoading,
    historyLoadingMore,
    fetchHistory,
  };
}
